/**
 * Status pill.
 *
 * The statuses are named by *meaning*, not colour, and the mapping lives here once. A caller that
 * could pass a colour would eventually make "failed" green somewhere, and the screen where that
 * happens is the one an administrator is using to decide whether a document published.
 *
 * `neutral` exists so "queued" is not orange. Something pending is not something wrong, and
 * colouring it as a warning trains people to ignore warnings.
 */

import { cx } from "./cx";

export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral";

export interface StatusPillProps {
  tone: StatusTone;
  children: React.ReactNode;
  className?: string;
}

// `neutral` is the only tone with a border. It is the lightest ground in the set and sits on
// surfaces nearly its own colour, so without one it has no edge and reads as unstyled text.
const TONES: Record<StatusTone, string> = {
  success: "bg-statusSuccessSubtle text-statusSuccess",
  warning: "bg-statusWarningSubtle text-statusWarning",
  danger: "bg-statusDangerSubtle text-statusDanger",
  info: "bg-statusInfoSubtle text-statusInfo",
  neutral: "border border-borderSubtle bg-statusNeutralSubtle text-statusNeutral",
};

export function StatusPill({ tone, children, className }: StatusPillProps): JSX.Element {
  return (
    <span
      className={cx(
        // Rounded rectangle rather than a capsule, and sentence case rather than uppercase: the
        // design puts these in a dense table column where a row of capsules reads as a row of
        // buttons, and uppercase Arabic does not exist — `text-transform` silently does nothing to
        // it, so an uppercase rule makes the two languages disagree about emphasis.
        "inline-flex items-center gap-2 rounded-sm px-2 py-1 text-label-sm font-medium normal-case",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
